import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, contactsApi, API_URL, getToken } from "./api";
import {
  lookupCallerBook,
  mergeCallerBook,
  saveCallerBookEntry,
} from "./callerBook";
import { phoneMatchKey } from "./shared";
import { useSession } from "./session";

export type LiveCall = {
  uuid: string;
  from: string;
  to: string;
  state: string;
  direction: string;
  duration: number | string;
  muted?: boolean;
  agentName?: string;
  agentExtension?: string;
  /** neron pbx_calls.id */
  id?: number | string;
};

export type CallFrom = "desk" | "sip";

export type CallPopup = {
  from: string;
  to: string;
  callId: string;
  direction: "inbound" | "outbound";
  state: string;
  duration: number;
  reason?: string;
  hunt?: string;
  callerName?: string;
  callerPhone?: string;
  callerCompany?: string;
  lastCall?: {
    at: string;
    direction: string;
    state: string;
    duration: number;
    hangupCause: string;
    agentName: string;
    agentExtension: string;
  } | null;
};

type CallMeta = {
  crmContactId?: string;
  name?: string;
  company?: string;
};

type CallCtx = {
  live: LiveCall[];
  popup: CallPopup | null;
  dismissPopup: () => void;
  clickToCall: (phone: string, meta?: string | CallMeta) => Promise<void>;
  hangup: (uuid: string) => Promise<void>;
  mute: (uuid: string, muted: boolean) => Promise<void>;
  myCall?: LiveCall;
  callFrom: CallFrom;
  setCallFrom: (next: CallFrom) => Promise<void>;
  extension: string;
  lineStatus: string;
  freeLine: () => Promise<void>;
};

const Ctx = createContext<CallCtx | null>(null);

const ACTIVE = new Set([
  "requested",
  "published",
  "acknowledged",
  "extension_ringing",
  "customer_dialling",
  "customer_ringing",
  "answered",
]);

type PbxCall = {
  id: number;
  call_id?: string | null;
  call_status?: string;
  extension_number?: string;
  customer_number?: string;
  direction?: string;
  duration_seconds?: number | null;
};

type NameCache = { name: string; company?: string };

function mapPbx(row: PbxCall): LiveCall {
  const st = String(row.call_status || "");
  let state = "ringing";
  if (st === "answered") state = "answered";
  else if (["hungup", "completed", "failed", "timed_out", "busy", "no_answer"].includes(st)) {
    state = "ended";
  }
  return {
    id: row.id,
    uuid: String(row.call_id || row.id),
    from: String(row.extension_number || ""),
    to: String(row.customer_number || ""),
    state,
    direction: String(row.direction || "outbound"),
    duration: row.duration_seconds ?? 0,
    agentExtension: String(row.extension_number || ""),
  };
}

function normalizePhone(input: string) {
  let digits = String(input || "").replace(/\D/g, "");
  if (digits.startsWith("91") && digits.length === 12) digits = digits.slice(2);
  if (digits.startsWith("0") && digits.length === 11) digits = digits.slice(1);
  if (digits.length > 10 && /^[6-9]\d{9}$/.test(digits.slice(-10))) {
    digits = digits.slice(-10);
  }
  if (digits.length === 10) return `0${digits}`;
  return digits;
}

function parseMeta(meta?: string | CallMeta): CallMeta {
  if (!meta) return {};
  if (typeof meta === "string") return { crmContactId: meta };
  return meta;
}

function customerPhone(call: Pick<LiveCall, "to" | "from" | "direction">) {
  // pbx_calls always stores customer in customer_number → mapped to `to`
  return String(call.to || "").trim();
}

function buildPopup(
  call: LiveCall,
  names: Map<string, NameCache>,
  extras?: Partial<CallPopup>,
): CallPopup {
  const phone = customerPhone(call);
  const key = phoneMatchKey(phone);
  const cached = key ? names.get(key) : undefined;
  const inbound = String(call.direction || "").toLowerCase() === "inbound";
  return {
    from: call.from,
    to: call.to,
    callId: call.uuid,
    direction: inbound ? "inbound" : "outbound",
    state: call.state,
    duration: Number(call.duration) || 0,
    callerPhone: phone,
    callerName: extras?.callerName || cached?.name || "",
    callerCompany: extras?.callerCompany || cached?.company || "",
    reason: extras?.reason,
    hunt: extras?.hunt,
    lastCall: extras?.lastCall ?? null,
  };
}

export function CallProvider({ children }: { children: React.ReactNode }) {
  const { user } = useSession();
  const [live, setLive] = useState<LiveCall[]>([]);
  const [popup, setPopup] = useState<CallPopup | null>(null);
  const [callFrom, setCallFromState] = useState<CallFrom>("desk");
  const [lineStatus, setLineStatus] = useState("idle");
  const activeIdRef = useRef<number | null>(null);
  const nameCacheRef = useRef<Map<string, NameCache>>(new Map());
  const lookupInflight = useRef<Set<string>>(new Set());
  const dismissUntilIdleRef = useRef(false);
  const inboundHoldRef = useRef(false);
  const inboundHoldUntilRef = useRef(0);
  const lastOutboundAtRef = useRef(0);
  const lastEnrichKeyRef = useRef("");

  async function setCallFrom(next: CallFrom) {
    setCallFromState(next);
    localStorage.setItem("ipbs_call_from", next);
  }

  function rememberName(phone: string, name?: string, company?: string) {
    const key = phoneMatchKey(phone);
    if (!key || !name?.trim()) return;
    nameCacheRef.current.set(key, {
      name: name.trim(),
      company: company?.trim() || undefined,
    });
    saveCallerBookEntry(phone, name, company);
  }

  async function hydrateCallerDirectory() {
    const { allCallerBookEntries } = await import("./callerBook");
    for (const row of allCallerBookEntries()) {
      const key = phoneMatchKey(row.phone);
      if (!key || !row.name) continue;
      nameCacheRef.current.set(key, {
        name: row.name,
        company: row.company,
      });
    }

    const absorb = (
      rows: Array<{ phone?: string; mobile?: string; name?: string; company?: string }>,
    ) => {
      mergeCallerBook(rows);
      for (const row of rows) {
        const phone = String(row.phone || row.mobile || "");
        const key = phoneMatchKey(phone);
        const name = String(row.name || "").trim();
        if (!key || !name) continue;
        nameCacheRef.current.set(key, {
          name,
          company: String(row.company || "").trim() || undefined,
        });
        saveCallerBookEntry(phone, name, row.company);
      }
    };

    try {
      const list = await contactsApi<Array<{ name?: string; phone?: string; company?: string }>>(
        "/contacts",
      );
      if (Array.isArray(list)) absorb(list);
    } catch {
      /* ignore */
    }

    for (const client of [contactsApi, api] as const) {
      try {
        const dir = await client<Array<{ name?: string; mobile?: string }>>("/users/directory");
        if (Array.isArray(dir)) {
          absorb(dir.map((u) => ({ name: u.name, phone: u.mobile, mobile: u.mobile })));
        }
      } catch {
        /* route may be missing on production until deploy */
      }
    }
  }

  function showIncomingCard(opts: {
    callId?: string | number | null;
    callDbId?: number | null;
    extension?: string;
    phone: string;
    callerName?: string;
    reason?: string;
  }) {
    const phone = String(opts.phone || "").trim();
    if (!phone && !opts.callId) return;
    const cached = lookupCallerBook(phone) || nameCacheRef.current.get(phoneMatchKey(phone) || "");
    if (opts.callerName) rememberName(phone, opts.callerName);
    else if (cached?.name) rememberName(phone, cached.name, cached.company);
    const ext = String(opts.extension || user?.extension || "");
    const mapped: LiveCall = {
      id: opts.callDbId || `inbound-${opts.callId || phone}`,
      uuid: String(opts.callId || opts.callDbId || phone || "inbound"),
      from: ext,
      to: phone,
      state: "ringing",
      direction: "inbound",
      duration: 0,
      agentExtension: ext,
    };
    dismissUntilIdleRef.current = false;
    inboundHoldRef.current = true;
    inboundHoldUntilRef.current = Date.now() + 10000;
    if (opts.callDbId) activeIdRef.current = Number(opts.callDbId);
    setLineStatus("ringing");
    setLive([mapped]);
    setPopup(
      buildPopup(mapped, nameCacheRef.current, {
        callerName: opts.callerName || cached?.name || "",
        callerCompany: cached?.company || "",
        reason: opts.reason || "",
      }),
    );
    if (phone) void enrichCaller(phone);
  }

  function recentOutbound() {
    return Date.now() - lastOutboundAtRef.current < 90000;
  }

  function inferDirection(row: PbxCall | LiveCall): "inbound" | "outbound" {
    const dir = String((row as PbxCall).direction || (row as LiveCall).direction || "").toLowerCase();
    if (dir === "inbound") return "inbound";
    if (dir === "outbound" || recentOutbound()) return "outbound";
    return "inbound";
  }

  async function findLastCall(phone: string): Promise<CallPopup["lastCall"]> {
    const key = phoneMatchKey(phone);
    if (!key) return null;
    try {
      const rows = await api<PbxCall[]>("/pbx/calls");
      const list = Array.isArray(rows) ? rows : [];
      const hit = list.find((c) => {
        const same = phoneMatchKey(String(c.customer_number || "")) === key;
        if (!same) return false;
        return !ACTIVE.has(String(c.call_status || ""));
      });
      if (!hit) return null;
      return {
        at: String(
          (hit as PbxCall & { ended_at?: string; answered_at?: string; started_at?: string; created_at?: string })
            .ended_at ||
            (hit as PbxCall & { answered_at?: string }).answered_at ||
            (hit as PbxCall & { started_at?: string }).started_at ||
            (hit as PbxCall & { created_at?: string }).created_at ||
            new Date().toISOString(),
        ),
        direction: String(hit.direction || "outbound"),
        state: String(hit.call_status || ""),
        duration: Number(hit.duration_seconds || 0) || 0,
        hangupCause: String((hit as PbxCall & { hangup_cause?: string }).hangup_cause || ""),
        agentName: "",
        agentExtension: String(hit.extension_number || ""),
      };
    } catch {
      return null;
    }
  }

  async function enrichCaller(phone: string) {
    const key = phoneMatchKey(phone);
    if (!key || key.length < 8) return;
    if (lookupInflight.current.has(key)) return;
    lookupInflight.current.add(key);
    lastEnrichKeyRef.current = key;
    try {
      const cached = nameCacheRef.current.get(key) || lookupCallerBook(phone);
      let name = cached?.name || "";
      let company = cached?.company || "";
      let lastCall: CallPopup["lastCall"] = null;

      const applyName = (n?: string, c?: string) => {
        if (n?.trim()) name = n.trim();
        if (c?.trim()) company = c.trim();
      };

      // Prefer immediate cache so the card never flashes "Unknown"
      if (name) {
        setPopup((prev) => {
          if (!prev) return prev;
          if (phoneMatchKey(prev.callerPhone || prev.to) !== key) return prev;
          if (prev.callerName) return prev;
          return { ...prev, callerName: name, callerCompany: company || prev.callerCompany };
        });
      }

      for (const client of [contactsApi, api] as const) {
        try {
          const row = await client<{
            name?: string;
            company?: string;
            lastCall?: CallPopup["lastCall"];
          }>(`/pbx/caller-lookup?phone=${encodeURIComponent(phone)}`);
          applyName(row?.name, row?.company);
          if (row?.lastCall) lastCall = row.lastCall;
          if (name) break;
        } catch {
          /* try next host */
        }
      }

      if (!name) {
        try {
          const list = await contactsApi<Array<{ name?: string; phone?: string; company?: string }>>(
            `/contacts?q=${encodeURIComponent(key)}`,
          );
          const hit = (Array.isArray(list) ? list : []).find(
            (c) => phoneMatchKey(String(c.phone || "")) === key,
          );
          applyName(hit?.name, hit?.company);
        } catch {
          /* ignore */
        }
      }

      if (!name) {
        for (const client of [contactsApi, api] as const) {
          try {
            const dir = await client<Array<{ name?: string; mobile?: string }>>("/users/directory");
            const hit = (Array.isArray(dir) ? dir : []).find(
              (u) => phoneMatchKey(String(u.mobile || "")) === key,
            );
            applyName(hit?.name);
            if (name) break;
          } catch {
            /* ignore */
          }
        }
      }

      if (!lastCall) lastCall = await findLastCall(phone);
      if (name) rememberName(phone, name, company);

      setPopup((prev) => {
        if (!prev) return prev;
        if (phoneMatchKey(prev.callerPhone || prev.to) !== key) return prev;
        return {
          ...prev,
          callerName: name || prev.callerName || "",
          callerCompany: company || prev.callerCompany || "",
          callerPhone: prev.callerPhone || phone,
          lastCall: lastCall || prev.lastCall || null,
        };
      });
    } finally {
      lookupInflight.current.delete(key);
    }
  }

  async function lookupCallerName(phone: string) {
    await enrichCaller(phone);
  }

  async function refreshLive() {
    if (!user?.extension) {
      setLive([]);
      return;
    }
    try {
      const ext = await api<{
        current_status?: string;
        active_call?: PbxCall | null;
      }>(`/pbx/extensions/${encodeURIComponent(user.extension)}`);
      setLineStatus(String(ext.current_status || "idle"));
      const lineIdle =
        !ext.current_status ||
        ["idle", "unknown", "unavailable"].includes(
          String(ext.current_status).toLowerCase(),
        );
      if (ext.active_call && ACTIVE.has(String(ext.active_call.call_status))) {
        const mapped = mapPbx(ext.active_call);
        mapped.direction = inferDirection(ext.active_call);
        if (inboundHoldRef.current) mapped.direction = "inbound";
        setLive([mapped]);
        activeIdRef.current = Number(ext.active_call.id);
        if (mapped.direction === "inbound") {
          inboundHoldRef.current = true;
          inboundHoldUntilRef.current = Date.now() + 10000;
          const phone = customerPhone(mapped);
          setPopup((prev) => {
            const next = buildPopup(mapped, nameCacheRef.current, {
              callerName: prev?.callerName,
              callerCompany: prev?.callerCompany,
              lastCall: prev?.lastCall,
            });
            return next;
          });
          if (phone) void enrichCaller(phone);
        }
        return;
      }

      // Extension ringing but active_call missing — scan recent PBX rows.
      const st = String(ext.current_status || "").toLowerCase();
      if (!recentOutbound() && (st === "ringing" || st === "inuse" || st === "busy")) {
        try {
          const rows = await api<PbxCall[]>("/pbx/calls");
          const mine = (Array.isArray(rows) ? rows : []).find(
            (c) =>
              String(c.extension_number || "") === String(user.extension) &&
              ACTIVE.has(String(c.call_status || "")),
          );
          if (mine) {
            const dir = inferDirection(mine);
            if (dir === "inbound") {
              showIncomingCard({
                callId: mine.call_id,
                callDbId: mine.id,
                extension: String(user.extension),
                phone: String(mine.customer_number || ""),
              });
              return;
            }
            const mapped = mapPbx(mine);
            mapped.direction = dir;
            setLive([mapped]);
            activeIdRef.current = Number(mine.id);
            return;
          }
        } catch {
          /* ignore */
        }
      }
      // Keep the outbound dial card alive while PBX still reports idle
      // (extension status often lags behind click-to-call).
      if (activeIdRef.current) {
        const row = await api<PbxCall>(`/pbx/calls/${activeIdRef.current}`).catch(() => null);
        if (row && ACTIVE.has(String(row.call_status))) {
          setLive([mapPbx(row)]);
          return;
        }
        if (row && !ACTIVE.has(String(row.call_status))) {
          activeIdRef.current = null;
          inboundHoldRef.current = false;
          setLive([]);
          return;
        }
        // Lookup failed — keep showing the local live row briefly.
        return;
      }
      // PBX/CRM idle with no tracked call — close the notification.
      if (lineIdle) {
        if (activeIdRef.current) {
          const row = await api<PbxCall>(`/pbx/calls/${activeIdRef.current}`).catch(() => null);
          if (row && ACTIVE.has(String(row.call_status))) {
            const mapped = mapPbx(row);
            mapped.direction = inferDirection(row);
            setLive([mapped]);
            return;
          }
        }
        // Brief grace only right after an inbound SSE (before PBX row appears).
        if (
          inboundHoldRef.current &&
          Date.now() < inboundHoldUntilRef.current
        ) {
          return;
        }
        activeIdRef.current = null;
        inboundHoldRef.current = false;
        inboundHoldUntilRef.current = 0;
        setLive([]);
        setPopup(null);
        return;
      }
      if (
        inboundHoldRef.current &&
        Date.now() < inboundHoldUntilRef.current
      ) {
        return;
      }
      inboundHoldRef.current = false;
      inboundHoldUntilRef.current = 0;
      setLive([]);
      setPopup(null);
    } catch {
      /* keep last */
    }
  }

  useEffect(() => {
    if (!user) return;
    void hydrateCallerDirectory();
    void refreshLive();
    const t = window.setInterval(() => void refreshLive(), 2000);

    // Optional SSE for live updates
    let es: EventSource | null = null;
    const token = getToken();
    if (token && typeof EventSource !== "undefined") {
      try {
        es = new EventSource(
          `${API_URL}/api/pbx/events?access_token=${encodeURIComponent(token)}`,
        );
        es.addEventListener("pbx_call", (ev) => {
          try {
            const payload = JSON.parse((ev as MessageEvent).data);
            const data = payload?.data || payload;
            const dir = String(data?.direction || "").toLowerCase();
            const extn = String(data?.extension_number || data?.extension || "");
            if (
              (payload?.type === "inbound_ringing" || dir === "inbound") &&
              extn &&
              String(user.extension) === extn
            ) {
              showIncomingCard({
                callId: data.call_id || data.callId,
                callDbId: data.id ? Number(data.id) : null,
                extension: extn,
                phone: String(data.customer_number || data.callerPhone || ""),
                callerName: String(data.caller_name || data.callerName || ""),
              });
            }
          } catch {
            /* ignore */
          }
          void refreshLive();
        });
        es.addEventListener("pbx_extension", (ev) => {
          try {
            const payload = JSON.parse((ev as MessageEvent).data);
            if (String(payload.extension) === String(user.extension)) {
              setLineStatus(String(payload.status || "idle"));
              const st = String(payload.status || "").toLowerCase();
              if (st === "ringing" || st === "inuse" || st === "busy") {
                void refreshLive();
              }
            }
          } catch {
            /* ignore */
          }
        });
        es.addEventListener("pbx_inbound", (ev) => {
          try {
            const payload = JSON.parse((ev as MessageEvent).data);
            const preview = payload?.preview || {};
            const hunt: Array<{ extension?: string }> = Array.isArray(preview.hunt)
              ? preview.hunt
              : [];
            const targets = [
              payload.extension,
              preview.firstExtension,
              ...hunt.map((h) => h.extension),
            ]
              .filter(Boolean)
              .map(String);
            if (!targets.includes(String(user.extension))) return;
            showIncomingCard({
              callId: payload.callId,
              callDbId: payload.callDbId ? Number(payload.callDbId) : null,
              extension: String(user.extension),
              phone: String(
                payload.callerPhone || preview.callerDial || preview.caller || "",
              ),
              callerName: String(payload.callerName || preview.callerName || ""),
              reason: String(preview.reason || ""),
            });
            void refreshLive();
          } catch {
            /* ignore */
          }
        });
      } catch {
        /* ignore */
      }
    }

    return () => {
      window.clearInterval(t);
      if (es) es.close();
    };
  }, [user?.id, user?.extension]);

  const myCall = live.find((c) => c.state !== "ended");

  // Drive the right-side call card from the live line (inbound + outbound).
  useEffect(() => {
    if (!myCall) {
      if (dismissUntilIdleRef.current) {
        dismissUntilIdleRef.current = false;
      }
      // Auto-close when the line is gone. Keep only a short outbound connect window.
      setPopup((prev) => {
        if (!prev) return null;
        if (
          prev.direction === "outbound" &&
          activeIdRef.current &&
          Date.now() - lastOutboundAtRef.current < 10000
        ) {
          return prev;
        }
        inboundHoldRef.current = false;
        inboundHoldUntilRef.current = 0;
        return null;
      });
      return;
    }
    if (dismissUntilIdleRef.current) return;
    const phone = customerPhone(myCall);
    setPopup((prev) => {
      const next = buildPopup(myCall, nameCacheRef.current, {
        callerName: prev?.callerName,
        callerCompany: prev?.callerCompany,
        reason: prev?.reason,
        lastCall: prev?.lastCall,
      });
      const sameParty =
        prev &&
        phoneMatchKey(prev.callerPhone || prev.to) === phoneMatchKey(phone);
      if (sameParty && prev.direction === "inbound") {
        next.direction = "inbound";
      }
      if (inboundHoldRef.current) {
        next.direction = "inbound";
      }
      if (sameParty && prev.callerName && !next.callerName) {
        next.callerName = prev.callerName;
        next.callerCompany = prev.callerCompany || next.callerCompany;
      }
      if (sameParty && prev.lastCall && !next.lastCall) {
        next.lastCall = prev.lastCall;
      }
      return next;
    });
    void enrichCaller(phone);
  }, [
    myCall?.uuid,
    myCall?.id,
    myCall?.state,
    myCall?.direction,
    myCall?.to,
    myCall?.from,
    myCall?.duration,
  ]);

  const value = useMemo<CallCtx>(
    () => ({
      live,
      popup,
      dismissPopup: () => {
        dismissUntilIdleRef.current = Boolean(myCall);
        setPopup(null);
      },
      myCall,
      callFrom,
      setCallFrom,
      extension: user?.extension || "",
      lineStatus,
      async freeLine() {
        if (!user?.extension) return;
        await api(`/pbx/extensions/${encodeURIComponent(user.extension)}/release`, {
          method: "POST",
        });
        setLineStatus("idle");
        activeIdRef.current = null;
        inboundHoldRef.current = false;
        setLive([]);
        setPopup(null);
        dismissUntilIdleRef.current = false;
      },
      async clickToCall(number, meta) {
        if (!user?.extension) {
          throw new Error("No extension mapped to your user. Ask an admin.");
        }
        const info = parseMeta(meta);
        const phone = normalizePhone(number);
        rememberName(phone, info.name, info.company);
        const hasLiveChannel = Boolean(
          myCall?.uuid || (myCall?.id && String(myCall.state) !== "ended"),
        );
        const optimistic: LiveCall = {
          id: `pending-${Date.now()}`,
          uuid: `pending-${Date.now()}`,
          from: user.extension,
          to: phone,
          state: "ringing",
          direction: "outbound",
          duration: 0,
          agentExtension: user.extension,
        };
        dismissUntilIdleRef.current = false;
        inboundHoldRef.current = false;
        lastOutboundAtRef.current = Date.now();
        setLive([optimistic]);
        setLineStatus("ringing");
        setPopup(
          buildPopup(optimistic, nameCacheRef.current, {
            callerName: info.name || "",
            callerCompany: info.company || "",
          }),
        );
        try {
          const row = await api<PbxCall>("/pbx/calls", {
            method: "POST",
            body: JSON.stringify({
              extension: user.extension,
              phoneNumber: phone,
              forceRelease: hasLiveChannel,
              contactId: info.crmContactId || undefined,
            }),
          });
          activeIdRef.current = Number(row.id);
          const mapped = mapPbx(row);
          setLive([mapped]);
          setPopup(
            buildPopup(mapped, nameCacheRef.current, {
              callerName: info.name || "",
              callerCompany: info.company || "",
            }),
          );
          void lookupCallerName(phone);
          void refreshLive();
        } catch (err) {
          activeIdRef.current = null;
          inboundHoldRef.current = false;
          setLive([]);
          setPopup(null);
          setLineStatus("idle");
          throw err;
        }
      },
      async hangup(uuid) {
        const current =
          live.find((c) => c.uuid === uuid || String(c.id) === uuid) || myCall;
        if (current?.id) {
          try {
            await api(`/pbx/calls/${current.id}/hangup`, { method: "POST" });
          } catch {
            if (user?.extension) {
              await api(`/pbx/extensions/${encodeURIComponent(user.extension)}/release`, {
                method: "POST",
              });
            }
          }
        } else if (user?.extension) {
          await api(`/pbx/extensions/${encodeURIComponent(user.extension)}/release`, {
            method: "POST",
          });
        }
        activeIdRef.current = null;
        inboundHoldRef.current = false;
        setLineStatus("idle");
        setLive([]);
        setPopup(null);
        dismissUntilIdleRef.current = false;
        await refreshLive();
      },
      async mute() {
        throw new Error("Mute is not available in broker click-to-call mode.");
      },
    }),
    [live, popup, myCall, callFrom, user?.extension, lineStatus],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCalls() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCalls outside provider");
  return ctx;
}
